import type { AddonsPublicos } from '@/lib/addons';
import { apiClient } from '../api/client';
import { logger } from '../logger';

// Types baseados no backend
export interface StoreDomain {
  domain: string;
  isPrimary: boolean;
}

/**
 * O que a vitrine recebe do plano do lojista: só o que ela desenha.
 *
 * O endpoint público mandava os 19 campos de `limits` — cotas de produto, de
 * sub-usuário, de armazenamento, direito a domínio próprio e a API — para
 * qualquer visitante de qualquer loja. Nada disso era lido aqui, e é
 * informação comercial do lojista. Ver `StorePresenter.toPublicHTTP` na API.
 *
 * Os dois que ficaram são decisões de renderização, e ambos já são visíveis a
 * olho nu para quem abre a página:
 *
 * - `hasWatermark` — a marca d'água aparece? (`MainLayout`)
 * - `maxWikiPages` — existe wiki nesta loja? Lido só como `!== 0`, nunca
 *   como número. (`Header`, `WikiPage`)
 *
 * Se um campo novo for preciso aqui, ele tem que ser liberado do lado da API
 * primeiro: o presenter público é uma allowlist, não um filtro.
 */
export interface StorePlanLimits {
  hasWatermark?: boolean;
  maxWikiPages?: number;
}

/** Só `limits`: nome, preço e datas do plano não saem para a vitrine. */
export interface StorePlan {
  limits: StorePlanLimits;
}

export interface StoreColors {
  colorPrimary?: string | null;
  colorPrimaryForeground?: string | null;
  colorPrimaryHover?: string | null;
  colorAccent?: string | null;
  colorAccentForeground?: string | null;
  colorAccentHover?: string | null;
  colorBackground?: string | null;
  colorBackgroundSecondary?: string | null;
  colorForeground?: string | null;
  colorCard?: string | null;
  colorCardForeground?: string | null;
  colorBorder?: string | null;
  colorMuted?: string | null;
  colorMutedForeground?: string | null;
  colorIcon?: string | null;
}

export type AdPlacement = 'FOOTER_BANNER' | 'IN_FEED' | 'ARTICLE_END';

/** Um anúncio próprio como a API manda: sem o link de destino, só o caminho do clique. */
export interface HouseAd {
  id: string;
  placements: AdPlacement[];
  locales: string[];
  title: string | null;
  description: string | null;
  cta: string | null;
  alt: string;
  imageUrl: string | null;
  imageMobileUrl: string | null;
  imageSquareUrl: string | null;
  /** Faixa própria do fim do conteúdo (antes usava a do rodapé). */
  imageArticleUrl: string | null;
  imageArticleMobileUrl: string | null;
  weight: number;
  /** Relativo à API (`/public/ads/<id>/click?store=`); a vitrine acrescenta a posição. */
  clickPath: string;
}

export interface StoreAds {
  placements: AdPlacement[];
  /** % das vezes para o anúncio próprio quando o Google também pode (0 a 100). */
  houseShare?: number;
  /** Null quando o Google não vale aqui (domínio próprio, Google desligado, loja sem produto). */
  google?: { client: string; slots: Partial<Record<AdPlacement, string>> } | null;
  house: HouseAd[];
}

export interface Store {
  id: string;
  name: string;
  slogan: string | null;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  /** Tipo da loja. DIGITAL não tem servidor, nick nem skin. */
  gameType?: 'HYTALE' | 'MINECRAFT' | 'DIGITAL';
  /** Layout da vitrine (`src/templates`). Ausente ou desconhecido: clássico. */
  template?: string;
  /** Modo de cor em que a vitrine abre, escolhido pelo lojista. */
  colorMode?: 'dark' | 'light';
  /**
   * O fundo do layout, para templates que têm um (o céu do Celeste): cor da
   * marca (`auto`), cor escolhida ou imagem enviada, e o padrão por cima.
   */
  templateBackground?: {
    type: 'auto' | 'color' | 'image';
    color: string | null;
    imageUrl: string | null;
    pattern: boolean;
  } | null;
  /** Módulos ligados. Ausente em resposta antiga: ver `lib/addons`. */
  addons?: AddonsPublicos;
  /** Widgets de terceiro ligados, já validados pelo backend. */
  widgets?: Array<{ widget: string; config: Record<string, string> }>;
  /**
   * A loja precisa mostrar o banner de consentimento?
   *
   * Derivado no backend a partir dos widgets ligados — não é um interruptor
   * que o lojista possa desligar. Ver docs/widgets/PLANO-WIDGETS.md §5.
   */
  requiresConsentBanner?: boolean;
  /**
   * Anúncios próprios da loja em plano gratuito, já decididos pela API.
   * `null` (ou ausente) em loja paga: não há anúncio para ligar nem esconder.
   * Ver `templates/comum/anuncios`.
   */
  ads?: StoreAds | null;
  heroTitle?: string;
  heroSubtitle?: string;
  heroBackgroundUrl?: string;
  showServerStatus?: boolean;
  showServerIp?: boolean;
  showPlayerCount?: boolean;
  showTopBuyer?: boolean;
  serverIp?: string;
  showDiscordWidget?: boolean;
  discordServerId?: string;
  showTopBuyersWidget?: boolean;
  showTopMonthWidget?: boolean;
  /** Página Jogadores: quem está jogando e o tempo de jogo, pelo plugin. */
  showOnlinePlayers?: boolean;
  defaultCurrency: 'BRL' | 'USD' | 'EUR';
  domains: StoreDomain[];
  planId: string | null;
  plan: StorePlan | null;
  colors?: StoreColors | null;
  isActive: boolean;
  isSuspended: boolean;
  suspensionReason?: string | null;
  scheduledDeletionAt?: string | null;
  createdAt: Date;
  updatedAt: Date;
  storeSettings?: {
    description?: string | null;
    contactEmail?: string | null;
    instagramUrl?: string | null;
    discordUrl?: string | null;
    twitterUrl?: string | null;
    tiktokUrl?: string | null;
    telegramUrl?: string | null;
    whatsappUrl?: string | null;
  } | null;
}

export interface StoreSettings {
  id: string;
  storeId: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  backgroundImage: string | null;
  heroTitle: string | null;
  heroSubtitle: string | null;
  serverIp: string | null;
  discordUrl: string | null;
  updatedAt: Date;
}

export interface Category {
  id: string;
  storeId: string;
  name: string;
  slug: string;
  description: string | null;
  imageUrl: string | null;
  bannerUrl: string | null;
  order: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Product {
  id: string;
  storeId: string;
  categoryId: string;
  name: string;
  slug: string;
  description: string | null;
  shortDescription: string | null;
  price: number;
  imageUrl: string | null;
  commands: string[];
  isActive: boolean;
  isFeatured: boolean;
  order: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface GoalReward {
  description: string;
  targetAmount: number;
  order: number;
}

export interface Goal {
  id: string;
  storeId: string;
  isActive: boolean;
  targetAmount: number;
  currentAmount: number;
  startsAt: string;
  endsAt: string;
  rewards: GoalReward[];
  createdAt: string;
  updatedAt: string;
}

export interface StoreTerms {
  termsOfService?: string;
  privacyPolicy?: string;
  serverRules?: string;
  dataCollection?: string;
}

export const storeService = {
  /**
   * Busca loja por domínio (público - sem autenticação)
   */
  async getByDomain(domain: string): Promise<Store> {
    logger.log('🔍 [storeService] Fetching store by domain:', domain);
    const url = `/public/stores/domain/${encodeURIComponent(domain)}`;
    const result = await apiClient.get<Store>(url);
    return result;
  },

  /**
   * Busca configurações da loja (público)
   */
  async getSettings(storeId: string): Promise<StoreSettings> {
    return apiClient.get<StoreSettings>(`/stores/${storeId}/settings`);
  },

  /**
   * Lista categorias da loja (público)
   */
  async getCategories(storeId: string): Promise<Category[]> {
    return apiClient.get<Category[]>(`/stores/${storeId}/categories`);
  },

  /**
   * Lista produtos da loja (público)
   */
  async getProducts(storeId: string, categoryId?: string): Promise<Product[]> {
    const params = categoryId ? `?categoryId=${categoryId}` : '';
    return apiClient.get<Product[]>(`/stores/${storeId}/products${params}`);
  },

  /**
   * Busca meta ativa da loja (público)
   */
  async getActiveGoal(storeId: string): Promise<Goal | null> {
    try {
      const response = await apiClient.get<{ goal: Goal | null }>(`/public/stores/${storeId}/goals/active`);
      return response.goal;
    } catch (error) {
      // Se não houver meta ativa, retorna null
      return null;
    }
  },

  /**
   * Busca termos de serviço, política de privacidade, regras e o resumo de
   * coleta de dados da loja (público)
   */
  async getTerms(storeId: string): Promise<StoreTerms> {
    return apiClient.get<StoreTerms>(`/public/stores/${storeId}/terms`);
  },
};
